import type { NavigationContainerRef } from '@react-navigation/native';
import type { RootStackParamList } from './types';
import { buildAppRootResetAction, MAIN_STACK_INITIAL_ROUTE, AUTH_STACK_INITIAL_ROUTE } from './rootNavigationAuth';
import { getAuthGuardReset } from '../shared/navigation/audienceGuard';

let root_ref: NavigationContainerRef<RootStackParamList> | null = null;
/** Queued until the matching Auth/Main stack is mounted and can handle the reset. */
let pending_auth_reset: boolean | null = null;

const MAX_AUTH_RESET_ATTEMPTS = 40;

function isNavigationRootReady(): boolean {
  return root_ref?.isReady() === true;
}

/** True when the currently mounted root stack can handle the target home route. */
function canHandleAuthHomeReset(is_authenticated: boolean): boolean {
  if (!isNavigationRootReady() || root_ref == null) {
    return false;
  }
  const state = root_ref.getRootState();
  const route_names = state?.routeNames ?? [];
  const target = is_authenticated ? MAIN_STACK_INITIAL_ROUTE : AUTH_STACK_INITIAL_ROUTE;
  return route_names.includes(target);
}

/** Returns true when the reset was dispatched. */
function dispatchAuthRootReset(is_authenticated: boolean): boolean {
  if (!canHandleAuthHomeReset(is_authenticated)) {
    pending_auth_reset = is_authenticated;
    return false;
  }
  pending_auth_reset = null;
  root_ref!.dispatch(buildAppRootResetAction(is_authenticated));
  return true;
}

export function setNavigationRootRef(ref: NavigationContainerRef<RootStackParamList> | null): void {
  root_ref = ref;
}

/** Call from `NavigationContainer` `onReady` — ref exists before the tree is navigable. */
export function notifyNavigationRootReady(): void {
  flushPendingAuthRootReset();
}

/** After Auth↔Main stack swap mounts; also used by `onReady`. */
export function flushPendingAuthRootReset(): void {
  if (pending_auth_reset !== null) {
    dispatchAuthRootReset(pending_auth_reset);
  }
}

export function getNavigationRootRef(): NavigationContainerRef<RootStackParamList> | null {
  return root_ref;
}

/**
 * Send user to guest Main or authenticated MainTabs → Dashboard.
 * Retries until Auth↔Main stack swap has mounted the target routes (avoids
 * “RESET … MainTabs was not handled” while AuthStack is still current).
 */
export function resetRootNavigationForAuth(is_authenticated: boolean, attempt = 0): void {
  pending_auth_reset = is_authenticated;
  if (dispatchAuthRootReset(is_authenticated)) {
    return;
  }
  if (attempt < MAX_AUTH_RESET_ATTEMPTS) {
    requestAnimationFrame(() => resetRootNavigationForAuth(is_authenticated, attempt + 1));
  }
}

/** Reset when root stack (Auth vs Main) mismatches session — navigation state changes. */
export function applyAuthNavigationGuard(is_authenticated: boolean): void {
  if (!isNavigationRootReady()) {
    return;
  }
  const state = root_ref?.getRootState();
  const { shouldReset, resetAction } = getAuthGuardReset(state, is_authenticated);
  if (shouldReset && resetAction) {
    root_ref?.dispatch(resetAction);
  }
}
